import { _ } from "../../core/polyglot/polyglot.js";
import { formatAsNumeralDate } from "../../core/helpers/date-formatting-util.js";
import {
	formatFullWithTwoDecimalsNoCurrency,
	getCurrencySymbol,
} from "../../core/helpers/currency-formatting-util.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import ArrayStream from "../../core/list/streams/ArrayStream.js";
import DimensionSpecification from "../../models/dimensionSpecification/DimensionSpecification.js";
import GridColumnSpecification from "../../core/widgets/list/GridColumnSpecification.js";
import linkedDocumentService from "../shared/linkedDocumentService.js";
import ListSource from "../../core/list/ListSource.js";
import makeTableListWidget from "../../core/widgets/list/TableListWidget/makeTableListWidget.js";
import notifier from "../../core/widgets/notifier/notifier.js";

/**
 * A widget with a table displaying business activities.
 */
export default class BusinessActivityWidget extends Widget {
	constructor(spec = {}) {
		super(spec);

		this._dimensionIdentifier = undefined;

		const messageForEmptyContent = (html) => {
			html.div(
				{ klass: "business-insights", "data-test": "insights" },
				html.div(
					{ klass: "insight" },
					html.h3(
						_(
							"Click on a date in the chart above to show business activity for that specific date.",
						),
					),
				),
			);
		};

		this._gridWidget = makeTableListWidget({
			columnSpecifications: this._generateColumnSpecs(),
			striped: true,
			messageForEmptyContent,
		});
	}

	showEvents({ businessEvents, dimensionIdentifier }) {
		this._dimensionIdentifier = dimensionIdentifier;

		this._gridWidget.setSource(
			ListSource.makeAndFetch({
				stream: new ArrayStream({ data: businessEvents }),
			}),
		);

		this.update();
	}

	setEmptySource() {
		this._gridWidget.setEmptySource();
		this.update();
	}

	renderContentOn(html) {
		html.div({ "data-test": "events" }, (html) => {
			html.div({ klass: "table-responsive" }, this._gridWidget);
		});
	}

	_linkedDocumentRenderer(event) {
		return (html) => {
			const ledgerEntry = event.getLedgerEntry();
			if (!ledgerEntry.hasLinkedDocument()) {
				return;
			}
			if (linkedDocumentService.haveLinkedDocumentIntegration()) {
				html.a(
					{
						href: linkedDocumentService.getByLedgerEntry(
							ledgerEntry,
						),
						target: "_blank",
					},
					html.i({ class: "fp-icon advisory-report-icon" }),
				);
			} else {
				html.a(html.i({ class: "fp-icon advisory-report-icon" })).click(
					() => {
						notifier.error(
							_(
								"Please re-authenticate by using the import wizard or contact your local support.",
							),
						);
					},
				);
			}
		};
	}

	_eventTypeRenderer(event) {
		return (html) => {
			html.span({ klass: ["icon", "icon-stop", event.getType()] });
			html.render(" ");
			html.render(event.getGroupName());
		};
	}

	_eventTransactionRenderer(event) {
		return (html) => {
			const transactionId = event.getTransactionId();

			html.div(
				html.a({ target: "_blank" }, transactionId).href(
					this._linkTo("ledger-entry", {
						fiscalYearId: event.getFiscalYearId(),
						ledgerId: transactionId,
						dimensionIdentifier: this._dimensionIdentifier,
					}),
				),
			);
		};
	}

	_eventAccountNumberRenderer(event) {
		return (html) => {
			const accountNumber = event.getAccountNumber();

			html.a({ target: "_blank" }, accountNumber).href(
				this._linkTo("account-statement", {
					accountNumber,
					dimensionSpecification:
						this._dimensionIdentifier &&
						new DimensionSpecification({
							dimensionId: this._dimensionIdentifier,
						}).toString(),
				}),
			);
		};
	}

	_eventDateRenderer(event) {
		return (html) =>
			html.span(formatAsNumeralDate(event.getDate())).attr({
				nowrap: "nowrap",
			});
	}

	_eventAmountRenderer(event) {
		return (html) => {
			html.span(
				formatFullWithTwoDecimalsNoCurrency(-event.getAmount()),
			).attr({ nowrap: "nowrap" });
		};
	}

	_generateColumnSpecs() {
		return [
			new GridColumnSpecification({
				name: _("Type"),
				sortCriterion: "GroupName",
				dataTestName: "type",
				renderer: this._eventTypeRenderer.bind(this),
			}),
			new GridColumnSpecification({
				name: _("Transaction text"),
				dataTestName: "text-left",
				headerCssClass: "text-left",
				sortCriterion: "TransactionText",
				renderer: (event) => event.getTransactionText(),
			}),
			new GridColumnSpecification({
				name: _("Transaction"),
				cssClass: "text-right",
				headerCssClass: "text-right",
				sortCriterion: "TransactionId",
				type: "number",
				renderer: this._eventTransactionRenderer.bind(this),
			}),
			new GridColumnSpecification({
				name: _(""),
				cssClass: "text-left no-left-padding",
				renderer: this._linkedDocumentRenderer.bind(this),
			}),
			new GridColumnSpecification({
				name: _("Account number"),
				headerCssClass: "text-right",
				cssClass: "text-right",
				sortCriterion: "AccountNumber",
				type: "number",
				renderer: this._eventAccountNumberRenderer.bind(this),
			}),
			new GridColumnSpecification({
				name: _("Account name"),
				headerCssClass: "text-left",
				cssClass: "text-left",
				sortCriterion: "AccountName",
				renderer: (event) => event.getAccountName(),
			}),
			new GridColumnSpecification({
				name: _("Date"),
				headerCssClass: "text-left",
				cssClass: "text-left",
				sortCriterion: "Date",
				type: "number",
				renderer: this._eventDateRenderer.bind(this),
			}),
			new GridColumnSpecification({
				name: _("Amount ({currency})", {
					currency: getCurrencySymbol(),
				}),
				headerCssClass: "text-right",
				cssClass: "text-right",
				sortCriterion: "Amount",
				type: "amount",
				renderer: this._eventAmountRenderer.bind(this),
				dataTestName: "amount",
			}),
		];
	}
}
